import { TagTypesKey } from '@/constants/api';
import { LanguageStatus } from '@/constants/enum';
import { getAppParams } from '@/helpers';
import { IResponse, IResponseInfo } from '@/types';
import { ILanguageList, ILanguageSettingsPayload, ILanguageTranslations, ISaveTranslationPayload } from '@/types/language';
import { IUpdateDiscountCode, IUpdatePlan, IUpdatePlanParams } from '@/types/account';
import {
  DeliveryTimeByLocationChart,
  FulfillmentPerformanceData,
  IAiInsightsData,
  IAiInsightsParams,
  IChartParams,
  IDeliveryTimeByCityParams,
  IIntegrateGoogleAnalyticsPayload,
  ShipmentsOverviewData,
} from '@/types/analytics';
import {
  IBlacklisting,
  IBlacklistingPayload,
  ICityPayload,
  ICreatedBlacklistWordPair,
  IDeletedBlacklistWordPair,
  IUpdatedBlacklistWordPair,
} from '@/types/blacklisting';
import { IDashboardShipment, IDashboardShipmentOvertime, IDashboardShipmentParams, IUserPlan } from '@/types/dashboard';
import { ICreatedTransitTime, IDeletedTransitTime, IEddSettings, IEddSettingsPayload, IUpdatedTransitTime } from '@/types/edd';
import {
  IEmailAccountPayload,
  IEmailDisconnectParams,
  INotificationAccounts,
  INotificationLogs,
  INotificationLogsParams,
  IResetToDefaultPayload,
  ISendTestEmailPayload,
  ISesConnectPayload,
  ISmsCredit,
  ISmsHistory,
  ISmsOrderStatus,
  ISmsParams,
  ISmsPricing,
  ISmsSummary,
  ITemplateDetail,
  ITemplateDetailParams,
  ITemplateDetailPayload,
} from '@/types/notification';
import { ICourierNamePayload, IProcessOldOrderPayload } from '@/types/order';
import {
  IConfirmSyncWindowPayload,
  IHistoricalSyncEstimate,
  IResyncEstimatePayload,
  IResyncHistoricalPayload,
  IResyncHistoricalResponse,
} from '@/types/historical-sync';
import {
  ICreateReviewTableParams,
  IGetReviewTable,
  IGetReviewTableParams,
  IUpdateDisplayReviewTableParams,
} from '@/types/reviewTable';
import {
  ICourier,
  ICourierMappingPage,
  IRawCourierMap,
  IUnrecognizedCarrier,
  IRawCourierMask,
  IRawCourierOption,
  IGetOrderLookup,
  IGetSyncStatus,
  IGetTrackingLink,
  IIntegrationAvada,
  IIntegrationAvadaPayload,
  IIntegrationTrustpilot,
  IIntegrationTrustpilotConfig,
  IIntegrationTrustpilotLocation,
  IIntegrationTrustpilotTemplate,
  IIntegrationTrustpilotPayload,
  IIntegrationTrustpilotTestConnection,
  ISettingParams,
  IUpdateSyncStatusParams,
} from '@/types/settings';
import {
  IShipmentCount,
  IShipmentDetail,
  IShipmentDetailParams,
  IShipmentReSyncBody,
  IShipmentSearch,
  IShipmentSearchParams,
  IShipmentTrackingLogs,
  ITrackingLogsParams,
  IUnfulfilledShipmentDetail,
  IUnsupportedTrackingPayload,
} from '@/types/shipment';
import { IMediaUpload, IProductRecommendResponse, ITrackingPageResponse, ITranslation } from '@/types/trackingpage';
import { ITrackingStore } from '@/types/trackingStore';
import { IAiConnection } from '@/types/aiAssistants';
import { IGuideStatus, IOnboardingStep, IOnboardingStepBody, IUpdateGuideTaskBody } from '@/types/welcome';
import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery as baseQuery } from './fetchBase';

export const apiCaller = createApi({
  reducerPath: 'apiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: baseQuery(),
  tagTypes: [
    TagTypesKey.PLAN,
    TagTypesKey.DASHBOARD,
    TagTypesKey.REVIEW_TABLE,
    TagTypesKey.ANALYTICS,
    TagTypesKey.SHIPMENT,
    TagTypesKey.NOTIFICATION,
    TagTypesKey.SETTINGS,
    TagTypesKey.COURIER_MAPPING,
    TagTypesKey.BLACKLISTING,
    TagTypesKey.EDD,
    TagTypesKey.SMS,
    TagTypesKey.TRACKING_PAGE,
    TagTypesKey.TRACKING_LINK,
    TagTypesKey.ORDER_LOOKUP,
    TagTypesKey.TRANSLATION,
    TagTypesKey.ONBOARDING,
    TagTypesKey.SYNC_STATUS,
    TagTypesKey.AI_ASSISTANTS,
    TagTypesKey.LANGUAGES,
    TagTypesKey.PROXY_PATH,
  ],
  endpoints: (builder) => ({
    // DASHBOARD
    getUserPlan: builder.query<IResponse<IUserPlan>, { screen?: string }>({
      query(params) {
        return {
          url: `dashboard/user-plan`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.PLAN }],
    }),
    getDashboardShipment: builder.query<IResponse<IDashboardShipment[]>, IDashboardShipmentParams>({
      query(params) {
        return {
          url: `dashboard/shipment`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
    }),
    getShipmentOvertime: builder.query<IResponse<IDashboardShipmentOvertime>, IDashboardShipmentParams>({
      query(params) {
        return {
          url: `dashboard/shipment-overtime`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.DASHBOARD }],
    }),

    // REVIEW TABLE
    getReviewTable: builder.query<IResponse<IGetReviewTable>, IGetReviewTableParams>({
      query(params) {
        return {
          url: `review-table/search`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.REVIEW_TABLE }],
    }),

    createReviewTable: builder.mutation<IResponse<undefined>, ICreateReviewTableParams>({
      query(params) {
        return {
          url: `review-table/create`,
          method: 'POST',
          body: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.REVIEW_TABLE }],
    }),

    updateDisplayReviewTable: builder.mutation<IResponse<undefined>, IUpdateDisplayReviewTableParams>({
      query(params) {
        return {
          url: `review-table/update`,
          method: 'POST',
          body: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.REVIEW_TABLE }],
    }),

    //ANALYTICS
    getAnalyticsShipmentOverview: builder.query<IResponse<ShipmentsOverviewData>, IChartParams>({
      query(params) {
        return {
          url: `analytic/shipments-overview`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.ANALYTICS }],
    }),
    getAnalyticsFulfillmentPerformance: builder.query<IResponse<FulfillmentPerformanceData>, IChartParams>({
      query(params) {
        return {
          url: `analytic/fulfillment-performance`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.ANALYTICS }],
    }),
    getAnalyticsDeliveryTimeByCity: builder.query<IResponse<DeliveryTimeByLocationChart>, IDeliveryTimeByCityParams>({
      query(params) {
        return {
          url: `analytic/delivery-time-by-city`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
    }),
    getAiInsights: builder.mutation<IResponse<IAiInsightsData>, IAiInsightsParams>({
      query(params) {
        return {
          url: `analytic/ai-insights`,
          method: 'POST',
          body: getAppParams(params),
        };
      },
    }),

    // SHIPMENT
    getShipmentSearch: builder.query<IResponse<IShipmentSearch>, IShipmentSearchParams>({
      query(params) {
        return {
          url: `shipment/search`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),
    getShipmentCount: builder.query<IResponse<IShipmentCount>, Partial<IShipmentSearchParams>>({
      query(params) {
        return {
          url: `shipment/count`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),
    getShipmentDetail: builder.query<IResponse<IShipmentDetail>, IShipmentDetailParams>({
      query(params) {
        return {
          url: `shipment/detail`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),
    getUnfulfilledShipmentDetail: builder.query<IResponse<IUnfulfilledShipmentDetail>, IShipmentDetailParams>({
      query(params) {
        return {
          url: `shipment/unfulfilled-detail`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),
    getTrackingLogs: builder.query<IResponse<IShipmentTrackingLogs>, ITrackingLogsParams>({
      query(params) {
        return {
          url: `shipment/tracking-logs`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),
    setUnSupportedTracking: builder.mutation<IResponseInfo, IUnsupportedTrackingPayload>({
      query(params) {
        return {
          url: `shipment/set-unsupported-tracking`,
          method: 'POST',
          body: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),
    resyncShipmentDetail: builder.mutation<IResponseInfo, IShipmentReSyncBody>({
      query(body) {
        return {
          url: `shipment/resync`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),

    syncTrackingUrlToShopify: builder.mutation<IResponseInfo, { shop: string }>({
      query(body) {
        return {
          url: `shipment/sync-tracking-url-to-shopify`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),

    // ORDER
    updateCourierName: builder.mutation<IResponseInfo, ICourierNamePayload>({
      query(payload) {
        return {
          url: `order/update-courier`,
          method: 'PUT',
          body: getAppParams(payload),
        };
      },
      invalidatesTags: (result) => (result?.state === 1 ? [{ type: TagTypesKey.SHIPMENT }] : []),
    }),
    processOldOrder: builder.mutation<IResponseInfo, IProcessOldOrderPayload>({
      query(payload) {
        return {
          url: `order/process-old-order`,
          method: 'POST',
          body: getAppParams(payload),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SHIPMENT }],
    }),

    updatePlan: builder.mutation<IResponse<IUpdatePlan>, IUpdatePlanParams>({
      query(params) {
        return {
          url: `shop/change-plan`,
          method: 'POST',
          body: getAppParams(params),
        };
      },
      // A plan change can unpublish over-limit languages on the backend (downgrade), so the
      // cached language list must refetch too — otherwise it still shows them as published.
      invalidatesTags: [{ type: TagTypesKey.PLAN }, { type: TagTypesKey.ONBOARDING }, { type: TagTypesKey.LANGUAGES }],
    }),

    // MEDIA
    uploadMedia: builder.mutation<IResponse<void>, FormData>({
      query: (image) => {
        return {
          url: `media`,
          method: 'POST',
          body: image,
        };
      },
      invalidatesTags: [{ type: TagTypesKey.TRACKING_PAGE }],
    }),
    updateUploadMedia: builder.mutation<IResponseInfo, IMediaUpload>({
      query(params) {
        return {
          url: `media/update`,
          method: 'PUT',
          body: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.TRACKING_PAGE }],
    }),

    // NOTIFICATION
    getNotificationLogs: builder.query<IResponse<INotificationLogs>, INotificationLogsParams>({
      query(params) {
        return {
          url: `notification/logs/search`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
    }),

    getNotificationAccounts: builder.query<IResponse<INotificationAccounts>, void>({
      query() {
        return {
          url: `notification/accounts`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    disconnectEmail: builder.mutation<IResponseInfo, IEmailDisconnectParams>({
      query(params) {
        return {
          url: `notification/email/disconnect`,
          method: 'DELETE',
          params: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    connectSes: builder.mutation<IResponseInfo, ISesConnectPayload>({
      query(body) {
        return {
          url: `notification/email/ses/connect`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    resendSesEmail: builder.mutation<IResponseInfo, Pick<ISesConnectPayload, 'email'>>({
      query(body) {
        return {
          url: `notification/email/ses/resend-verify`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    updateEmailAccount: builder.mutation<IResponseInfo, IEmailAccountPayload>({
      query(body) {
        return {
          url: `notification/email/update`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    getTemplateDetail: builder.query<IResponse<ITemplateDetail>, ITemplateDetailParams>({
      query(params) {
        return {
          url: `notification/email/template/detail`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
      providesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    updateTemplate: builder.mutation<IResponseInfo, ITemplateDetailPayload>({
      query(body) {
        return {
          url: `notification/email/template/update`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    sendTestEmail: builder.mutation<IResponseInfo, ISendTestEmailPayload>({
      query(body) {
        return {
          url: `notification/email/ses/send-test`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
    }),

    resetToDefault: builder.mutation<IResponseInfo, IResetToDefaultPayload>({
      query(body) {
        return {
          url: `notification/email/template/reset`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.NOTIFICATION }],
    }),

    // TRACKING PAGE
    getTrackingPage: builder.query<IResponse<ITrackingPageResponse>, void | null>({
      query: () => {
        return {
          url: `page/search`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.TRACKING_PAGE }],
    }),

    getProductRecommend: builder.query<IResponse<IProductRecommendResponse>, void>({
      query: () => {
        return {
          url: `product/get-product-recommend`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    getTranslation: builder.query<IResponse<ITranslation>, { language: string }>({
      query: (language) => {
        return {
          url: `page/get-translation`,
          method: 'GET',
          params: getAppParams(language),
        };
      },
      providesTags: [{ type: TagTypesKey.TRANSLATION }],
    }),

    updateTranslation: builder.mutation<IResponse<void>, Object>({
      query: (data) => {
        return {
          url: `page/update-translation`,
          method: 'PUT',
          body: getAppParams(data),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.TRANSLATION }],
    }),

    getLanguages: builder.query<IResponse<ILanguageList>, void>({
      query: () => ({ url: `page/languages`, method: 'GET', params: getAppParams() }),
      providesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),
    addLanguage: builder.mutation<IResponse<unknown>, { language: string }>({
      query: (data) => ({ url: `page/languages`, method: 'POST', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),
    removeLanguage: builder.mutation<IResponse<unknown>, { language: string }>({
      query: (data) => ({ url: `page/languages`, method: 'DELETE', params: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),
    setLanguageStatus: builder.mutation<IResponse<unknown>, { language: string; status: LanguageStatus }>({
      query: (data) => ({ url: `page/languages/status`, method: 'PATCH', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),
    setDefaultLanguage: builder.mutation<IResponse<unknown>, { language: string }>({
      query: (data) => ({ url: `page/languages/default`, method: 'PATCH', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.LANGUAGES }, { type: TagTypesKey.TRANSLATION }],
    }),
    getLanguageTranslations: builder.query<IResponse<ILanguageTranslations>, { language: string }>({
      query: (data) => ({ url: `page/languages/translations`, method: 'GET', params: getAppParams(data) }),
      providesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),
    saveLanguageTranslations: builder.mutation<IResponse<unknown>, ISaveTranslationPayload>({
      query: (data) => ({ url: `page/languages/translations`, method: 'PUT', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),
    autoTranslateLanguage: builder.mutation<
      IResponse<{ [section: string]: Record<string, string> }>,
      { language: string } & Record<string, any>
    >({
      query: (data) => ({ url: `page/languages/auto-translate`, method: 'POST', body: getAppParams(data) }),
    }),
    updateLanguageSettings: builder.mutation<IResponse<unknown>, ILanguageSettingsPayload>({
      query: (data) => ({ url: `page/languages/settings`, method: 'PATCH', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.LANGUAGES }],
    }),

    updateTrackingPage: builder.mutation<IResponse<void>, Object>({
      query: (data) => {
        return {
          url: `page/update-tracking-page-setting`,
          method: 'PUT',
          body: getAppParams(data),
        };
      },
    }),

    getListCourier: builder.query<
      IResponse<{
        total: number;
        couriers: Array<ICourier>;
      }>,
      ISettingParams.IGetListCourier
    >({
      query: (data) => {
        return {
          url: `setting/courier`,
          method: 'GET',
          params: getAppParams(data),
        };
      },
    }),

    updateCourierSetting: builder.mutation<IResponse<void>, ISettingParams.IUpdateCourierSetting>({
      query: (data) => {
        return {
          url: `setting/courier`,
          method: 'POST',
          body: getAppParams(data),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    getCourierMapping: builder.query<IResponse<ICourierMappingPage>, void>({
      query: () => {
        return {
          url: `setting/courier-mapping`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),
    getRawCourierList: builder.query<IResponse<{ rawCouriers: Array<IRawCourierOption>; total: number }>, ISettingParams.IGetRawCourierOptions>({
      query: (data) => ({ url: `setting/raw-courier/list`, method: 'GET', params: getAppParams(data) }),
      providesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),
    getRawCourierOptions: builder.query<IResponse<{ rawCouriers: Array<IRawCourierOption>; total: number }>, ISettingParams.IGetRawCourierOptions>({
      query: (data) => ({ url: `setting/raw-courier/options`, method: 'GET', params: getAppParams(data) }),
      providesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    getRawCourierMasks: builder.query<IResponse<Array<IRawCourierMask>>, void>({
      query: () => ({ url: `setting/raw-courier/masks`, method: 'GET', params: getAppParams() }),
      providesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    createRawCourierMask: builder.mutation<IResponseInfo, Omit<IRawCourierMask, 'id'>>({
      query: (data) => ({ url: `setting/raw-courier/mask`, method: 'POST', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    updateRawCourierMask: builder.mutation<IResponseInfo, Pick<IRawCourierMask, 'id' | 'maskCourier'>>({
      query: ({ id, ...data }) => ({ url: `setting/raw-courier/mask/${id}`, method: 'PUT', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    deleteRawCourierMask: builder.mutation<IResponseInfo, number>({
      query: (id) => ({ url: `setting/raw-courier/mask/${id}`, method: 'DELETE', params: getAppParams() }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    getRawCourierMaps: builder.query<IResponse<Array<IRawCourierMap>>, void>({
      query: () => ({ url: `setting/raw-courier/maps`, method: 'GET', params: getAppParams() }),
      providesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    getUnrecognizedCarriers: builder.query<IResponse<Array<IUnrecognizedCarrier>> & { remainQuota: number | null }, void>({
      query: () => ({ url: `setting/raw-courier/unrecognized`, method: 'GET', params: getAppParams() }),
      providesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    createRawCourierMap: builder.mutation<IResponseInfo, Omit<IRawCourierMap, 'id'>>({
      query: (data) => ({ url: `setting/raw-courier/map`, method: 'POST', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    updateRawCourierMap: builder.mutation<IResponseInfo, Pick<IRawCourierMap, 'id' | 'mapCourier'>>({
      query: ({ id, ...data }) => ({ url: `setting/raw-courier/map/${id}`, method: 'PUT', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    deleteRawCourierMap: builder.mutation<IResponseInfo, number>({
      query: (id) => ({ url: `setting/raw-courier/map/${id}`, method: 'DELETE', params: getAppParams() }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }],
    }),

    mapAndRetrackRawCourier: builder.mutation<IResponseInfo, ISettingParams.IRetrackRawCourierMap>({
      query: (data) => ({ url: `setting/raw-courier/map/retrack`, method: 'POST', body: getAppParams(data) }),
      invalidatesTags: [{ type: TagTypesKey.COURIER_MAPPING }, { type: TagTypesKey.SHIPMENT }, { type: TagTypesKey.PLAN }],
    }),

    // SETTINGS
    getIntegrationsStatus: builder.query<IResponse<{ avada?: IIntegrationAvada; trustpilot?: IIntegrationTrustpilot }>, void>({
      query() {
        return {
          url: `integration/status`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    getIntegrationAvada: builder.query<IResponse<IIntegrationAvada>, void>({
      query() {
        return {
          url: `integration/avada`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    createIntegrationAvada: builder.mutation<IResponseInfo, IIntegrationAvadaPayload>({
      query(body) {
        return {
          url: `integration/avada`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    deleteIntegrationAvada: builder.mutation<IResponseInfo, void>({
      query() {
        return {
          url: `integration/avada`,
          method: 'DELETE',
          params: getAppParams(),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SETTINGS }],
    }),

    getIntegrationTrustpilot: builder.query<IResponse<IIntegrationTrustpilot>, void>({
      query() {
        return {
          url: `integration/trustpilot`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    createIntegrationTrustpilot: builder.mutation<IResponseInfo, IIntegrationTrustpilotPayload>({
      query(body) {
        return {
          url: `integration/trustpilot`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    deleteIntegrationTrustpilot: builder.mutation<IResponseInfo, void>({
      query() {
        return {
          url: `integration/trustpilot`,
          method: 'DELETE',
          params: getAppParams(),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    getIntegrationTrustpilotTemplates: builder.query<IResponse<IIntegrationTrustpilotTemplate[]>, void>({
      query() {
        return {
          url: `integration/trustpilot/templates`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),
    getIntegrationTrustpilotLocations: builder.query<IResponse<IIntegrationTrustpilotLocation[]>, void>({
      query() {
        return {
          url: `integration/trustpilot/locations`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),
    saveIntegrationTrustpilotConfig: builder.mutation<IResponseInfo, IIntegrationTrustpilotConfig>({
      query(body) {
        return {
          url: `integration/trustpilot/config`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SETTINGS }],
    }),
    testConnectionIntegrationTrustpilot: builder.mutation<IResponse<IIntegrationTrustpilotTestConnection>, IIntegrationTrustpilotPayload>({
      query(body) {
        return {
          url: `integration/trustpilot/test-connection`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
    }),

    getOrderLookup: builder.query<IResponse<IGetOrderLookup>, void>({
      query() {
        return {
          url: `setting/order-lookup`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.ORDER_LOOKUP }],
    }),

    getAppStatus: builder.query<IResponse<ISettingParams.IAppStatus>, void>({
      query() {
        return {
          url: `setting/app-status`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    updateOrderLookup: builder.mutation<IResponseInfo, ISettingParams.IUpdateOrderLookupParams>({
      query(data) {
        return {
          url: `setting/order-lookup`,
          method: 'PUT',
          body: getAppParams(data),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.ORDER_LOOKUP }],
    }),

    getTrackingLink: builder.query<IResponse<IGetTrackingLink>, void>({
      query() {
        return {
          url: `setting/tracking-link`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.TRACKING_LINK }],
    }),

    updateTrackingLink: builder.mutation<IResponse<void>, ISettingParams.IUpdateTrackingLinkParams>({
      query(data) {
        return {
          url: `setting/tracking-link`,
          method: 'PUT',
          body: getAppParams(data),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.TRACKING_LINK }],
    }),

    getProxyPath: builder.query<IResponse<{ proxyPath: string }>, void>({
      query() {
        return { url: `shop/proxy-path`, method: 'GET', params: getAppParams() };
      },
      providesTags: [{ type: TagTypesKey.PROXY_PATH }],
    }),

    updateProxyPath: builder.mutation<IResponse<void>, { proxyPath: string }>({
      query(data) {
        return { url: `shop/proxy-path`, method: 'PUT', body: getAppParams(data) };
      },
      invalidatesTags: [{ type: TagTypesKey.PROXY_PATH }],
    }),

    //ShopSettings
    getSyncStatus: builder.query<IResponse<IGetSyncStatus>, void>({
      query() {
        return {
          url: `shop/getSyncShopify`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.SYNC_STATUS }],
    }),

    updateSyncStatus: builder.mutation<IResponse<void>, IUpdateSyncStatusParams>({
      query(data) {
        return {
          url: `shop/updateSyncShopify`,
          method: 'PUT',
          body: getAppParams(data),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SYNC_STATUS }],
    }),

    // SMS
    getSmsOrderStatus: builder.query<IResponse<ISmsOrderStatus>, void>({
      query() {
        return {
          url: `notification/sms/setting/detail`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.SMS }],
    }),

    updateSmsOrderStatus: builder.mutation<IResponse<{ balanceAlert: boolean }>, ISmsParams.IUpdateSmsOrderStatus>({
      query(data) {
        return {
          url: `notification/sms/setting/update`,
          method: 'PUT',
          body: getAppParams(data),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.SMS }],
    }),

    getListPricing: builder.query<IResponse<Array<ISmsPricing>>, void>({
      query() {
        return {
          url: `notification/sms/pricing/list`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    getSmsHistory: builder.query<IResponse<ISmsHistory>, ISmsParams.IGetSmsHistoryParams>({
      query(data) {
        return {
          url: `notification/sms/history`,
          method: 'GET',
          params: getAppParams(data),
        };
      },
    }),

    getSmsSummary: builder.query<IResponse<ISmsSummary>, void>({
      query() {
        return {
          url: `notification/sms/summary`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    getSmsChargeLink: builder.query<IResponse<{ url: string }>, number>({
      query(amount) {
        return {
          url: `notification/sms/charge`,
          method: 'GET',
          params: getAppParams({ amount }),
        };
      },
    }),

    getSmsCredit: builder.query<IResponse<ISmsCredit>, void>({
      query() {
        return {
          url: `notification/sms/credit`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    // BLACKLISTING
    getBlacklisting: builder.query<IResponse<IBlacklisting>, void>({
      query() {
        return {
          url: `page/get-blacklist`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.BLACKLISTING }],
    }),

    updateBlacklisting: builder.mutation<IResponseInfo, IBlacklistingPayload>({
      query(body) {
        return {
          url: `page/update-blacklist`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.BLACKLISTING }],
    }),

    createBlacklistWordPair: builder.mutation<IResponseInfo, ICreatedBlacklistWordPair>({
      query(body) {
        return {
          url: `page/blacklist-wordpair`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.BLACKLISTING }],
    }),

    updateBlacklistWordPair: builder.mutation<IResponseInfo, IUpdatedBlacklistWordPair>({
      query(body) {
        return {
          url: `page/blacklist-wordpair`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.BLACKLISTING }],
    }),

    deleteBlacklistWordPair: builder.mutation<IResponseInfo, IDeletedBlacklistWordPair>({
      query(params) {
        return {
          url: `page/blacklist-wordpair`,
          method: 'DELETE',
          params: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.BLACKLISTING }],
    }),

    getListCountries: builder.query<IResponse<Array<string>>, void>({
      query() {
        return {
          url: `page/get-list-countries`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    getListCities: builder.query<IResponse<Array<string>>, ICityPayload>({
      query(params) {
        return {
          url: `page/get-list-cities`,
          method: 'GET',
          params: getAppParams(params),
        };
      },
    }),

    // EDD
    getEDDSettings: builder.query<IResponse<IEddSettings>, void>({
      query() {
        return {
          url: `page/edd-setting`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.EDD }],
    }),

    updateEddSettings: builder.mutation<IResponseInfo, IEddSettingsPayload>({
      query(body) {
        return {
          url: `page/edd-setting`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.EDD }],
    }),

    createTransitTime: builder.mutation<IResponseInfo, ICreatedTransitTime>({
      query(body) {
        return {
          url: `page/transit-time`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.EDD }],
    }),

    updateTransitTime: builder.mutation<IResponseInfo, IUpdatedTransitTime>({
      query(body) {
        return {
          url: `page/transit-time`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.EDD }],
    }),

    deleteTransitTime: builder.mutation<IResponseInfo, IDeletedTransitTime>({
      query(params) {
        return {
          url: `page/transit-time`,
          method: 'DELETE',
          params: getAppParams(params),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.EDD }],
    }),

    integrateGoogleAnalytics: builder.mutation<IResponseInfo, IIntegrateGoogleAnalyticsPayload>({
      query(body) {
        return {
          url: `page/integrate-ga`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
    }),

    updateDiscountCode: builder.mutation<IResponseInfo, IUpdateDiscountCode>({
      query(body) {
        return {
          url: `discount/apply-code`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.PLAN }],
    }),

    sendGa4AdminAnalytics: builder.mutation<IResponseInfo, { eventName: string }>({
      query(body) {
        return {
          url: `analytic/ga4`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
    }),

    getTrackingStore: builder.query<ITrackingStore, void>({
      query() {
        return {
          url: `/tracking-store/search`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.TRACKING_PAGE }],
    }),
    getOnboardingStep: builder.query<IResponse<IOnboardingStep>, void>({
      query() {
        return {
          url: `shop/onboarding`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.ONBOARDING }],
    }),
    updateOnboardingStep: builder.mutation<IResponse<IOnboardingStep>, IOnboardingStepBody>({
      query(body) {
        return {
          url: `shop/onboarding`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.ONBOARDING }],
    }),
    confirmSyncWindow: builder.mutation<IResponseInfo, IConfirmSyncWindowPayload>({
      query(body) {
        return {
          url: `shop/onboarding/confirm-sync-window`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.ONBOARDING }],
    }),
    resyncEstimate: builder.mutation<IHistoricalSyncEstimate, IResyncEstimatePayload>({
      query(body) {
        return {
          url: `shipment/resync-estimate`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
    }),
    resyncHistorical: builder.mutation<IResyncHistoricalResponse, IResyncHistoricalPayload>({
      query(body) {
        return {
          url: `shipment/resync-historical`,
          method: 'POST',
          body: getAppParams(body),
        };
      },
    }),

    getGuideTasks: builder.query<IResponse<IGuideStatus>, void>({
      query() {
        return {
          url: `shop/guide-tasks`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.ONBOARDING }],
    }),

    updateGuideTask: builder.mutation<IResponse<IGuideStatus>, IUpdateGuideTaskBody>({
      query(body) {
        return {
          url: `shop/guide-tasks`,
          method: 'PUT',
          body: getAppParams(body),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.ONBOARDING }],
    }),

    dismissGuide: builder.mutation<IResponse<{ state: number; msg: string }>, void>({
      query() {
        return {
          url: `shop/guide-tasks`,
          method: 'DELETE',
          params: getAppParams(),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.ONBOARDING }],
    }),

    getTokenSSOCanny: builder.query<IResponse<{ ssoToken: string }>, void>({
      query() {
        return {
          url: `shop/canny-sso`,
          method: 'GET',
          params: getAppParams(),
        };
      },
    }),

    // AI ASSISTANTS
    getAiConnections: builder.query<IResponse<IAiConnection[]>, void>({
      query() {
        return {
          url: `mcp/connections`,
          method: 'GET',
          params: getAppParams(),
        };
      },
      providesTags: [{ type: TagTypesKey.AI_ASSISTANTS }],
    }),
    deleteAiConnection: builder.mutation<IResponseInfo, { id: string }>({
      query({ id }) {
        return {
          url: `mcp/connections/${id}`,
          method: 'DELETE',
          params: getAppParams(),
        };
      },
      invalidatesTags: [{ type: TagTypesKey.AI_ASSISTANTS }],
    }),
  }),
});

export const {
  useGetUserPlanQuery,
  useLazyGetUserPlanQuery,
  useGetDashboardShipmentQuery,
  useGetReviewTableQuery,
  useCreateReviewTableMutation,
  useUpdateDisplayReviewTableMutation,
  useGetAnalyticsShipmentOverviewQuery,
  useGetAnalyticsFulfillmentPerformanceQuery,
  useLazyGetAnalyticsDeliveryTimeByCityQuery,
  useGetAiInsightsMutation,
  useGetShipmentSearchQuery,
  useGetShipmentCountQuery,
  useGetShipmentDetailQuery,
  useLazyGetShipmentDetailQuery,
  useGetUnfulfilledShipmentDetailQuery,
  useLazyGetUnfulfilledShipmentDetailQuery,
  useGetTrackingLogsQuery,
  useSetUnSupportedTrackingMutation,
  useUpdateCourierNameMutation,
  useProcessOldOrderMutation,
  useUpdatePlanMutation,
  useUploadMediaMutation,
  useUpdateUploadMediaMutation,
  useGetNotificationLogsQuery,
  useGetTrackingPageQuery,
  useGetProductRecommendQuery,
  useLazyGetTranslationQuery,
  useUpdateTranslationMutation,
  useGetLanguagesQuery,
  useAddLanguageMutation,
  useRemoveLanguageMutation,
  useSetLanguageStatusMutation,
  useSetDefaultLanguageMutation,
  useLazyGetLanguageTranslationsQuery,
  useSaveLanguageTranslationsMutation,
  useAutoTranslateLanguageMutation,
  useUpdateLanguageSettingsMutation,
  useUpdateTrackingPageMutation,
  useGetNotificationAccountsQuery,
  useDisconnectEmailMutation,
  useConnectSesMutation,
  useResendSesEmailMutation,
  useUpdateEmailAccountMutation,
  useGetTemplateDetailQuery,
  useLazyGetTemplateDetailQuery,
  useUpdateTemplateMutation,
  useSendTestEmailMutation,
  useResetToDefaultMutation,
  useGetIntegrationsStatusQuery,
  useGetIntegrationAvadaQuery,
  useCreateIntegrationAvadaMutation,
  useDeleteIntegrationAvadaMutation,
  useGetIntegrationTrustpilotQuery,
  useCreateIntegrationTrustpilotMutation,
  useDeleteIntegrationTrustpilotMutation,
  useTestConnectionIntegrationTrustpilotMutation,
  useLazyGetIntegrationTrustpilotTemplatesQuery,
  useLazyGetIntegrationTrustpilotLocationsQuery,
  useSaveIntegrationTrustpilotConfigMutation,
  useGetListCourierQuery,
  useUpdateCourierSettingMutation,
  useGetCourierMappingQuery,
  useGetRawCourierOptionsQuery,
  useGetRawCourierListQuery,
  useGetRawCourierMasksQuery,
  useCreateRawCourierMaskMutation,
  useUpdateRawCourierMaskMutation,
  useDeleteRawCourierMaskMutation,
  useGetRawCourierMapsQuery,
  useGetUnrecognizedCarriersQuery,
  useCreateRawCourierMapMutation,
  useUpdateRawCourierMapMutation,
  useDeleteRawCourierMapMutation,
  useMapAndRetrackRawCourierMutation,
  useGetOrderLookupQuery,
  useUpdateOrderLookupMutation,
  useUpdateSyncStatusMutation,
  useGetProxyPathQuery,
  useUpdateProxyPathMutation,
  useGetTrackingLinkQuery,
  useUpdateTrackingLinkMutation,
  useGetSmsOrderStatusQuery,
  useUpdateSmsOrderStatusMutation,
  useGetBlacklistingQuery,
  useUpdateBlacklistingMutation,
  useCreateBlacklistWordPairMutation,
  useUpdateBlacklistWordPairMutation,
  useDeleteBlacklistWordPairMutation,
  useGetListCountriesQuery,
  useGetListCitiesQuery,
  useGetSmsSummaryQuery,
  useGetSmsHistoryQuery,
  useGetListPricingQuery,
  useLazyGetSmsChargeLinkQuery,
  useGetSmsCreditQuery,
  useGetEDDSettingsQuery,
  useUpdateEddSettingsMutation,
  useUpdateTransitTimeMutation,
  useCreateTransitTimeMutation,
  useDeleteTransitTimeMutation,
  useIntegrateGoogleAnalyticsMutation,
  useResyncShipmentDetailMutation,
  useSyncTrackingUrlToShopifyMutation,
  useUpdateDiscountCodeMutation,
  useGetShipmentOvertimeQuery,
  useSendGa4AdminAnalyticsMutation,
  useGetOnboardingStepQuery,
  useUpdateOnboardingStepMutation,
  useConfirmSyncWindowMutation,
  useResyncEstimateMutation,
  useResyncHistoricalMutation,
  useGetGuideTasksQuery,
  useUpdateGuideTaskMutation,
  useDismissGuideMutation,
  useGetTrackingStoreQuery,
  useGetAppStatusQuery,
  useGetSyncStatusQuery,
  useGetTokenSSOCannyQuery,
  useGetAiConnectionsQuery,
  useDeleteAiConnectionMutation,
} = apiCaller;
